// src/components/ml/ModelRegistryPanel.js
// AI Intelligence Center → Model Registry: per model type — metric history, feature importance,
// every version (paged), promote / roll back / compare / archive / delete, drift, audit trail.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, ButtonGroup, Flex, Heading, Icon, IconButton, Menu,
  MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Select, SimpleGrid, Skeleton, Table, Tbody, Td, Text, Textarea, Th, Thead, Tooltip, Tr, VStack, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdArchive, MdCompareArrows, MdDelete, MdMoreVert, MdRefresh, MdRestore, MdRocketLaunch } from 'react-icons/md';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, ReferenceDot } from 'recharts';
import mlOpsService from '../../services/mlOpsService';
import { useMlEvents } from '../../hooks/useMlOps';
import { ExplainButton } from './FlowSenseAIExplainer';
import { EVENT_COLOR, EVENT_LABEL, STATUS_COLOR, TRIGGER_LABEL, fmtDate, fmtMetric, timeAgo } from './mlFormat';

const PAGE = 15;

function CompareModal({ isOpen, onClose, modelType, refA, metric }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!isOpen || !refA) return undefined;
    let alive = true;
    setData(null); setError(null);
    mlOpsService.compare(modelType, refA).then(d => { if (alive) setData(d); }).catch(e => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [isOpen, modelType, refA]);
  const f = (v) => (v === null || v === undefined ? '—' : Math.abs(v) <= 1 && metric?.format === 'ratio' ? `${(v * 100).toFixed(1)}%` : Number(v).toFixed(3));
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Compare versions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && <Alert status="error" borderRadius="md"><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>}
          {!data && !error && <Skeleton h="160px" />}
          {data && (
            <VStack align="stretch" spacing={3}>
              <Table size="sm">
                <Thead><Tr><Th>Metric</Th><Th isNumeric>v{data.a.model_version}</Th><Th isNumeric>{data.b ? `v${data.b.model_version} (production)` : 'production'}</Th></Tr></Thead>
                <Tbody>
                  {data.metrics.map(r => (
                    <Tr key={r.key}><Td>{r.key}</Td><Td isNumeric fontWeight={r.key === data.verdict.metric ? '700' : undefined}>{f(r.a)}</Td><Td isNumeric>{f(r.b)}</Td></Tr>
                  ))}
                </Tbody>
              </Table>
              <Alert status={data.verdict.decision === 'promote' ? 'success' : data.verdict.decision === 'rejected_safeguard' ? 'error' : 'info'} borderRadius="md">
                <AlertIcon /><AlertDescription fontSize="sm">{data.verdict.reason}</AlertDescription>
              </Alert>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function ModelRegistryPanel({ models = [], selected, onSelect, canManage, canDelete }) {
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [versions, setVersions] = useState({ items: [], total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, row, verdict }
  const [notes, setNotes] = useState('');
  const [compareRef, setCompareRef] = useState(null);
  const cancelRef = useRef(null);
  const mounted = useRef(true);

  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const grid = useColorModeValue('#E2E8F0', '#2D3748');
  const axis = useColorModeValue('#718096', '#A0AEC0');

  const type = selected || models[0]?.key;
  const model = models.find(m => m.key === type);
  const metric = detail?.metric || model?.metric;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const loadDetail = useCallback(async () => {
    if (!type) return;
    try {
      const d = await mlOpsService.getModelDetail(type);
      if (mounted.current) { setDetail(d); setDetailError(null); }
    } catch (e) { if (mounted.current) setDetailError(e.message); }
  }, [type]);

  const loadVersions = useCallback(async () => {
    if (!type) return;
    setLoadingVersions(true);
    try {
      const v = await mlOpsService.listModels({ modelType: type, status: statusFilter || undefined, limit: PAGE, offset: page * PAGE });
      if (mounted.current) setVersions(v);
    } catch (e) {
      if (mounted.current) toast({ title: 'Could not load versions', description: e.message, status: 'error', duration: 4000 });
    } finally { if (mounted.current) setLoadingVersions(false); }
  }, [type, statusFilter, page, toast]);

  useEffect(() => { setDetail(null); setPage(0); }, [type]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const refreshTimer = useRef(null);
  useMlEvents(useCallback((ev) => {
    if (ev.modelType && ev.modelType !== type && ev.modelType !== 'ALL') return;
    if (ev.event === 'job_update') return; // progress ticks don't change the registry
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => { loadDetail(); loadVersions(); }, 800);
  }, [type, loadDetail, loadVersions]));
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  const run = async (kind, row, opts = {}) => {
    setBusy(`${kind}:${row?.id || ''}`);
    try {
      if (kind === 'promote') await mlOpsService.promote(type, row.id, { force: Boolean(opts.force), notes: notes || undefined });
      if (kind === 'archive') await mlOpsService.archive(type, row.id);
      if (kind === 'delete') await mlOpsService.remove(type, row.id);
      if (kind === 'rollback') await mlOpsService.rollback(type);
      toast({ title: { promote: 'Promoted to production', archive: 'Version archived', delete: 'Version deleted', rollback: 'Rolled back' }[kind], status: 'success', duration: 3000 });
      setConfirm(null); setNotes('');
      loadDetail(); loadVersions();
    } catch (e) {
      if (kind === 'promote' && e.code === 'SAFEGUARD') { setConfirm({ kind: 'force', row, verdict: e.verdict }); return; }
      toast({ title: 'Action failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally { if (mounted.current) setBusy(null); }
  };

  const history = (detail?.history || []).map(h => ({ ...h, label: String(h.version).slice(-8), value: h.metric }));
  const fi = detail?.featureImportance && typeof detail.featureImportance === 'object'
    ? Object.entries(detail.featureImportance).map(([k, v]) => ({ feature: k, value: Math.abs(Number(v) || 0) })).sort((a, b) => b.value - a.value).slice(0, 12)
    : [];
  const champ = detail?.champion;
  const pages = Math.max(1, Math.ceil((versions.total || 0) / PAGE));

  return (
    <VStack align="stretch" spacing={4}>
      <Flex gap={2} wrap="wrap" align="center">
        <ButtonGroup size="sm" isAttached variant="outline" flexWrap="wrap">
          {models.map(m => <Button key={m.key} onClick={() => onSelect(m.key)} colorScheme={m.key === type ? 'purple' : 'gray'} variant={m.key === type ? 'solid' : 'outline'}>{m.name}</Button>)}
        </ButtonGroup>
        <Box flex="1" />
        {type && <ExplainButton topic={type} label="Explain this model" size="sm" variant="outline" />}
        <Tooltip label="Refresh" hasArrow><IconButton aria-label="Refresh" size="sm" icon={<Icon as={MdRefresh} />} onClick={() => { loadDetail(); loadVersions(); }} /></Tooltip>
      </Flex>

      {detailError && <Alert status="error" borderRadius="lg"><AlertIcon /><AlertDescription flex="1">{detailError}</AlertDescription><Button size="sm" onClick={loadDetail}>Retry</Button></Alert>}

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Text fontSize="xs" color={muted} textTransform="uppercase">Production version</Text>
          {!detail ? <Skeleton h="90px" mt={2} /> : champ ? (
            <>
              <Heading size="md" mt={1}>v{champ.model_version}</Heading>
              <Text fontSize="3xl" fontWeight="800" color="green.400">{fmtMetric(model?.champion?.metric ?? null, metric)}</Text>
              <Text fontSize="xs" color={muted}>{metric?.label} · live since {fmtDate(champ.activated_at)}{champ.promoted_by ? ` · by ${champ.promoted_by}` : ''}</Text>
              {champ.notes && <Text fontSize="xs" mt={2} noOfLines={3}>{champ.notes}</Text>}
            </>
          ) : <Text mt={2} fontSize="sm">No version in production yet — predictions use the built-in fallback.</Text>}
          {canManage && (
            <Button mt={4} size="sm" leftIcon={<Icon as={MdRestore} />} colorScheme="orange" variant="outline" onClick={() => setConfirm({ kind: 'rollback' })} isDisabled={!champ} isLoading={busy === 'rollback:'}>
              Roll back to previous
            </Button>
          )}
        </Box>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} gridColumn={{ xl: 'span 2' }}>
          <Text fontSize="xs" color={muted} textTransform="uppercase" mb={2}>{metric?.label || 'Metric'} by version ({metric?.higherIsBetter ? 'higher' : 'lower'} is better)</Text>
          {!detail ? <Skeleton h="200px" /> : history.length ? (
            <Box h="220px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: axis }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: axis }} domain={['auto', 'auto']} tickFormatter={v => (metric?.format === 'ratio' ? `${Math.round(v * 100)}%` : v)} />
                  <RTooltip formatter={(v) => fmtMetric(v, metric)} labelFormatter={(l, p) => (p?.[0]?.payload ? `v${p[0].payload.version} · ${p[0].payload.status}` : l)} />
                  <Line type="monotone" dataKey="value" stroke="#805AD5" strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
                  {history.filter(h => h.status === 'production').map(h => <ReferenceDot key={h.id} x={h.label} y={h.value} r={6} fill="#38A169" stroke="none" />)}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : <Text fontSize="sm" color={muted}>No versions yet.</Text>}
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={4}>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Text fontSize="xs" color={muted} textTransform="uppercase" mb={2}>What drives the production model</Text>
          {!detail ? <Skeleton h="200px" /> : fi.length ? (
            <Box h={`${Math.max(160, fi.length * 22)}px`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fi} layout="vertical" margin={{ left: 8, right: 8 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="feature" width={120} tick={{ fontSize: 10, fill: axis }} />
                  <RTooltip formatter={v => Number(v).toFixed(3)} />
                  <Bar dataKey="value" fill="#805AD5" radius={[0, 4, 4, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : <Text fontSize="sm" color={muted}>The production version did not report feature importance.</Text>}
        </Box>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Text fontSize="xs" color={muted} textTransform="uppercase" mb={2}>Drift & data</Text>
          {!detail ? <Skeleton h="120px" /> : (
            <VStack align="stretch" spacing={2} fontSize="sm">
              <Text><b>Training records:</b> {detail.data?.records?.toLocaleString?.() ?? '—'}</Text>
              {detail.data?.sources && Object.entries(detail.data.sources).map(([k, v]) => <Text key={k} fontSize="xs" color={muted}>{k}: {Number(v).toLocaleString()}</Text>)}
              <Text><b>Last milestone:</b> {detail.milestone ? `${Number(detail.milestone.records).toLocaleString()} records · ${timeAgo(detail.milestone.at)}` : 'not recorded yet'}</Text>
              <Text><b>Drift:</b> {!detail.drift?.ok ? `unavailable (${detail.drift?.error || 'ML engine offline'})` : detail.drift.report?.drift_detected ? `detected in ${(detail.drift.report.drift_features || []).join(', ')}` : detail.drift.report?.baseline === false ? 'no baseline yet (retrain to store one)' : `no drift reported${detail.drift.report?.baseline_features ? ` · ${detail.drift.report.baseline_features} features in baseline` : ''}`}</Text>
              <Text fontSize="xs" color={muted}>{detail.catalog?.dataSource}</Text>
            </VStack>
          )}
        </Box>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <Text fontSize="xs" color={muted} textTransform="uppercase" mb={2}>History</Text>
          <VStack align="stretch" spacing={2} maxH="240px" overflowY="auto">
            {(detail?.events || []).map(e => (
              <Flex key={e.id} gap={2} align="flex-start">
                <Badge colorScheme={EVENT_COLOR[e.event] || 'gray'} minW="88px" textAlign="center" fontSize="0.6em">{EVENT_LABEL[e.event] || e.event}</Badge>
                <Text fontSize="xs" flex="1">{e.model_version ? `v${e.model_version} · ` : ''}{e.actor_label || 'system'} · {timeAgo(e.created_at)}</Text>
              </Flex>
            ))}
            {detail && !detail.events?.length && <Text fontSize="sm" color={muted}>No history yet.</Text>}
          </VStack>
        </Box>
      </SimpleGrid>

      <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} overflowX="auto">
        <Flex mb={3} align="center" gap={3} wrap="wrap">
          <Heading size="sm">Versions</Heading>
          <Text fontSize="xs" color={muted}>{versions.total?.toLocaleString?.() || 0} total</Text>
          <Box flex="1" />
          <Select size="sm" maxW="180px" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} aria-label="Filter by status">
            <option value="">All statuses</option><option value="production">Production</option><option value="staging">Staging</option><option value="archived">Archived</option>
          </Select>
        </Flex>
        <Table size="sm">
          <Thead><Tr><Th>Version</Th><Th>Status</Th><Th isNumeric>{metric?.label}</Th><Th>Trained by</Th><Th>Created</Th><Th>Notes</Th><Th /></Tr></Thead>
          <Tbody>
            {loadingVersions && !versions.items.length ? [0, 1, 2].map(i => <Tr key={i}><Td colSpan={7}><Skeleton h="16px" /></Td></Tr>) : versions.items.map(row => (
              <Tr key={row.id}>
                <Td fontFamily="mono" fontSize="xs">{row.model_version}</Td>
                <Td><Badge colorScheme={STATUS_COLOR[row.status] || 'gray'}>{row.status}</Badge></Td>
                <Td isNumeric>{fmtMetric(row.performance_metrics?.[metric?.key] ?? row.performance_metric_value, metric)}</Td>
                <Td fontSize="xs">{TRIGGER_LABEL[row.trained_by] || row.trained_by || 'legacy'}</Td>
                <Td fontSize="xs">{fmtDate(row.created_at)}</Td>
                <Td fontSize="xs" maxW="280px"><Text noOfLines={2}>{row.notes || ''}</Text></Td>
                <Td>
                  <Menu isLazy placement="bottom-end">
                    <MenuButton as={IconButton} size="xs" variant="ghost" icon={<Icon as={MdMoreVert} />} aria-label="Version actions" isLoading={busy?.endsWith(`:${row.id}`)} />
                    <MenuList fontSize="sm">
                      <MenuItem icon={<Icon as={MdCompareArrows} />} onClick={() => setCompareRef(row.id)}>Compare with production</MenuItem>
                      {canManage && row.status !== 'production' && <MenuItem icon={<Icon as={MdRocketLaunch} />} onClick={() => setConfirm({ kind: 'promote', row })}>Promote to production</MenuItem>}
                      {canManage && row.status === 'staging' && <MenuItem icon={<Icon as={MdArchive} />} onClick={() => run('archive', row)}>Archive</MenuItem>}
                      {canDelete && row.status !== 'production' && <MenuItem icon={<Icon as={MdDelete} />} color="red.400" onClick={() => setConfirm({ kind: 'delete', row })}>Delete</MenuItem>}
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
            {!loadingVersions && !versions.items.length && <Tr><Td colSpan={7}><Text fontSize="sm" color={muted}>No versions{statusFilter ? ` with status ${statusFilter}` : ''}.</Text></Td></Tr>}
          </Tbody>
        </Table>
        <Flex justify="flex-end" align="center" gap={2} mt={3}>
          <Text fontSize="xs" color={muted}>Page {page + 1} of {pages}</Text>
          <Button size="xs" onClick={() => setPage(p => Math.max(0, p - 1))} isDisabled={page === 0}>Previous</Button>
          <Button size="xs" onClick={() => setPage(p => Math.min(pages - 1, p + 1))} isDisabled={page >= pages - 1}>Next</Button>
        </Flex>
      </Box>

      <CompareModal isOpen={Boolean(compareRef)} onClose={() => setCompareRef(null)} modelType={type} refA={compareRef} metric={metric} />

      <AlertDialog isOpen={Boolean(confirm)} leastDestructiveRef={cancelRef} onClose={() => { setConfirm(null); setNotes(''); }}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {{ promote: 'Promote to production?', force: 'Below the safeguard', delete: 'Delete this version?', rollback: 'Roll back?' }[confirm?.kind]}
            </AlertDialogHeader>
            <AlertDialogBody>
              {confirm?.kind === 'promote' && (
                <VStack align="stretch" spacing={3}>
                  <Text fontSize="sm">v{confirm.row.model_version} will serve every {model?.name} prediction. The current production version is archived and can be restored with Roll back.</Text>
                  <Textarea size="sm" placeholder="Reason (optional, saved in the audit trail)" value={notes} onChange={e => setNotes(e.target.value)} maxLength={400} />
                </VStack>
              )}
              {confirm?.kind === 'force' && <Text fontSize="sm">{confirm.verdict?.reason || 'This version does not meet the safeguard.'} Promote it anyway?</Text>}
              {confirm?.kind === 'delete' && <Text fontSize="sm">v{confirm.row.model_version} ({confirm.row.status}) is removed from the registry. This cannot be undone.</Text>}
              {confirm?.kind === 'rollback' && <Text fontSize="sm">The version that was in production before v{champ?.model_version} is restored and the ML engine reloads it.</Text>}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => { setConfirm(null); setNotes(''); }}>Cancel</Button>
              <Button ml={3} colorScheme={confirm?.kind === 'delete' || confirm?.kind === 'force' ? 'red' : 'purple'} isLoading={Boolean(busy)}
                onClick={() => {
                  if (confirm.kind === 'promote') run('promote', confirm.row);
                  else if (confirm.kind === 'force') run('promote', confirm.row, { force: true });
                  else if (confirm.kind === 'delete') run('delete', confirm.row);
                  else if (confirm.kind === 'rollback') run('rollback', null);
                }}>
                {{ promote: 'Promote', force: 'Promote anyway', delete: 'Delete', rollback: 'Roll back' }[confirm?.kind]}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
}
